import { customFieldText, normalizeCustomFieldName } from './custom-field-mapping'
import type { PublicCustomField } from './property-custom-fields'

/**
 * Paste hygiene for image-typed custom fields (dashboard#1574).
 *
 * This is the one place that hygiene lives. The shapes `parseImageFieldValue`
 * handles came from four rounds on the spaces widget's `bedroomNimage` field:
 * dashboard#1497 (run-on pastes, separators, pasted markup), #1519 (rooted
 * paths, already-proxied hrimgs.co values), #1564 (protocol-relative kept,
 * proxy-host casing) and #1571 (unwrap a pasted `plain/` proxy URL so the
 * render sizes it once).
 *
 * `normalizeImageCustomFields` applies the parser at the import boundary —
 * `usePropertyCustomFields`'s `select` (common/property-custom-fields.ts) —
 * so every widget that reads a custom field off that hook sees a clean
 * value already. The parser stays idempotent on its own output, so a caller
 * holding a raw record (a test, any future direct consumer) still gets the
 * same answer.
 */

// ── Photo-field parsing (port of BedroomShowcase\Images::parse) ──────
//
// The `bedroomNimage` field was designed for one URL, but operators paste
// several — one per line, comma/pipe/semicolon-separated, or as one run-on
// string with no separator at all. Splitting on a separator alone cannot
// find that last shape, so the split happens ahead of every URL scheme
// instead, and separators are only trimmed off the pieces. Commas are NOT
// treated as separators inside a URL: CDN transform chains (`w_300,h_200`)
// carry them legitimately.

export const IMAGE_FIELD_MAX_URLS = 20
/**
 * Bound on the RAW `bedroomNimage` value, for the same reason its sibling
 * `sanitizeBedroomDetails` (in `bedroom-showcase.ts`) bounds its own input:
 * this runs inside `resolveBedroomMedia` -> the widget's `mediaBySpace` memo ->
 * `renderToString` on the renderer's PDP route, once per bedroom. 20 URLs at
 * 2KB each is under 40KB, so this is generous headroom over any real value.
 */
export const IMAGE_FIELD_MAX_INPUT = 20_000
const LEADING_SEPARATORS_RE = /^[\s,;|]+/
const TRAILING_SEPARATORS_RE = /[\s,;|]+$/

/** `name="value"` inside pasted markup. Quoted values only — an unquoted one cannot be told from following text. */
const IMAGE_ATTR_RE = /(?:^|[\s/])([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*(?:"([^"]*)"|'([^']*)')/g

function looksLikeUrl(url: string): boolean {
  // `\S*` for the path allowed `<`, `>` and quotes, so a URL harvested out of
  // the wrong attribute could carry the rest of the markup with it.
  return /^https?:\/\/[^\s/<>"']+\.[^\s/<>"']+(\/[^\s<>"']*)?$/i.test(url)
}

/**
 * Any `scheme:` prefix other than a bare relative/protocol-relative path.
 * http(s) is handled by the sibling branch via `looksLikeUrl`; this guards
 * the SCHEME-LESS branch (and the legacy single-token passthrough) against
 * `javascript:`/`data:`/anything else being accepted as an "image path"
 * just because it happens to end in an image extension.
 *
 * Strips leading ASCII control characters and spaces first — the same
 * `\x00-\x20\x7f` class `common/sanitize/content-html.ts`'s `safeContentHref`
 * strips, since URL parsers ignore them at the start of a scheme too, so
 * `\0javascript:evil.jpg` must be judged by the scheme it actually resolves
 * to, not by the literal characters before it (Codex-flagged LOW, fresh
 * pass 2026-09-12; the current `<img src>` sink does not execute
 * `javascript:` regardless, so this closes the gap between the check's
 * guarantee and its implementation rather than a live exploit).
 */
function hasDangerousScheme(token: string): boolean {
  return /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(token.replace(/^[\x00-\x20\x7f]+/, ''))
}

/**
 * Extension-less, host-qualified CDN path (`cdn.example.com/img/abc123`): no
 * scheme, no leading slash, but the host segment has a dot. Deliberately
 * narrower than "any single token with no separators": that admitted every
 * bare word an operator types into an unfilled field — `TBD`, `N/A`, `none`,
 * `-`, `0`, `?` — each of which became a broken <img> with a clickable
 * lightbox trigger on the guest-facing room card, plus a spurious request
 * resolved against the page URL (`?` re-requested the whole page as an
 * image). A deliberate deviation from the toolkit's looser passthrough; the
 * scheme-less branch one line up already refuses free text for exactly this
 * reason.
 *
 * dashboard#1519: this used to also accept a ROOTED path (`/cdn/img/abc123`)
 * — getProxiedImageUrl prefixes `https://` onto any scheme-less URL, so a
 * rooted value collided into a broken `https:///cdn/img/abc...`. Rejected
 * now by isUnresolvableRootedPath at the call site below (resolving a rooted
 * path correctly needs the property's site origin, which this widget does
 * not have). The protocol-relative form (`//cdn.example.com/img/abc123`)
 * stays accepted: getProxiedImageUrl prefixes `https:` onto a `//` value, so
 * it resolved on develop and still does (#1564 review).
 */
const LEGACY_PATH_RE = /^(?:\/\/)?[^\s,;|<>"'?/]*\.[^\s,;|<>"'?/]+\/[^\s,;|<>"'?]+$/

/**
 * A same-origin rooted path (`/cdn/img/abc`): exactly one leading `/`.
 * dashboard#1519: getProxiedImageUrl prefixes `https://` onto any
 * scheme-less URL, so a rooted value collides into a broken
 * `https:///cdn/img/abc...`. Resolving it correctly needs the property's
 * site origin, which this widget does not have; rejecting it sends the slot
 * to the property gallery fallback instead of a broken <img> (a follow-up if
 * Central ever starts emitting rooted paths and a site origin becomes
 * available to resolve against). A protocol-relative value (`//host/a.jpg`)
 * is NOT rooted: getProxiedImageUrl turns it into `https://host/a.jpg`, which
 * rendered fine on develop, so it is left alone (#1564 review).
 */
function isUnresolvableRootedPath(token: string): boolean {
  return /^\/(?!\/)/.test(token)
}

/**
 * dashboard#1519: an operator-pasted value can already BE an hrimgs.co proxy
 * URL in getProxiedImageUrl's own shape, `https://hrimgs.co/<options>/plain/
 * <original URL>`. The parser splits ahead of every scheme, so that value
 * arrives as two pieces: the proxy prefix ending in `/plain/` and the
 * original URL. The prefix is dropped here (it is not an image on its own,
 * and pushing it double-proxied into the gray placeholder), and the original
 * URL is kept as the image, so the render proxies it ONCE with the size it
 * needs (a `w:640` paste no longer upscales into the lightbox, a `w:2000`
 * paste no longer downloads in full for a thumbnail; #1564 review). Only the
 * `plain/` form is unwrapped: imgproxy's signed or base64 forms carry no
 * readable original and are left to the render-side pass-through
 * (common/image-proxy.ts getProxiedImageUrlOnce).
 *
 * The check is a prefix test plus indexOf, never a lookbehind or an
 * unbounded backtracking regex: it runs once per bedroom on the renderer's
 * SSR path, and a variable-length lookbehind here measured 52.8 ms at the
 * 20,000-byte input cap (4x per doubling) in the #1564 review.
 */
const PROXY_HOST_PREFIX_RE = /^https?:\/\/hrimgs\.co\//i
const PROXY_PLAIN_SEGMENT = '/plain/'

/** `''` when the piece is a bare proxy prefix (its original URL is the next piece), `null` when it is not a proxy URL at all. */
function unwrapProxiedUrl(piece: string): string | null {
  if (!PROXY_HOST_PREFIX_RE.test(piece)) return null
  const at = piece.toLowerCase().indexOf(PROXY_PLAIN_SEGMENT)
  if (at === -1) return null
  return stripProxyFormatSuffix(piece.slice(at + PROXY_PLAIN_SEGMENT.length))
}

/** imgproxy allows a trailing `@<format>` on the plain URL; the original never carries it. */
function stripProxyFormatSuffix(url: string): string {
  return url.replace(/@[a-z0-9]+$/i, '')
}

function looksLikeImagePath(token: string): boolean {
  if (token === '' || /[\s<>"']/.test(token) || hasDangerousScheme(token) || isUnresolvableRootedPath(token)) return false
  return /\.(?:jpe?g|png|gif|webp|avif|svg)(?:[?#]\S*)?$/i.test(token)
}

/**
 * Parse a raw `bedroomNimage` field value into a list of image URLs.
 * Mirrors `Images::parse` field-for-field.
 */
export function parseImageFieldValue(raw: string): string[] {
  const trimmed = raw.trim().slice(0, IMAGE_FIELD_MAX_INPUT)
  if (trimmed === '') return []

  // Pasted markup (<img src>, <a href>): the URLs live in attributes. Matched
  // by exact attribute NAME — `\b(?:src|href)=` was satisfied after any
  // non-word character, so `data-src=` counted, and a literal `src=` inside a
  // `title` value counted too (and then swallowed the rest of the markup,
  // because the URL check allowed `<`, `>` and quotes).
  let text = trimmed
  if (text.includes('<')) {
    const urlsFromAttrs: string[] = []
    IMAGE_ATTR_RE.lastIndex = 0
    let m: RegExpExecArray | null
    while ((m = IMAGE_ATTR_RE.exec(text)) !== null) {
      const name = m[1].toLowerCase()
      if (name !== 'src' && name !== 'href') continue
      const value = m[2] ?? m[3] ?? ''
      if (value !== '') urlsFromAttrs.push(value)
    }
    if (urlsFromAttrs.length > 0) text = urlsFromAttrs.join('\n')
  }

  // A rich-text editor may have stored `&amp;` in query strings, and an
  // editor that re-encoded an already-encoded value stores `&amp;amp;`. The
  // whole chain collapses to one `&` in a single pass: decoding one layer per
  // call made the parser non-idempotent on its own output, so the hook's
  // canonical value re-parsed to a different URL than the raw paste did
  // (dashboard#1574 verifier). No `&amp;` survives this line, so the entity
  // decodes below reach a fixed point in one pass.
  text = text
    .replace(/&amp;(?:amp;)*/g, '&')
    .replace(/&#x27;|&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')

  const urls: string[] = []

  // Split AHEAD of every scheme — the only split that finds a run-on value
  // with no separator at all; each piece then starts with a URL (except
  // possibly the first, which may be leading junk). An already-proxied
  // hrimgs.co paste splits at its wrapped URL's own scheme into the proxy
  // prefix and the original URL; see unwrapProxiedUrl for why that is the
  // wanted outcome (dashboard#1519).
  const pieces = text.split(/(?=https?:\/\/)/i)
  // Set by a bare proxy prefix: the piece that follows is the original URL
  // it wrapped, so it gets the `@<format>` strip the prefix could not apply.
  let afterProxyPrefix = false

  for (let piece of pieces) {
    // Two anchored passes, not one alternation: `/^[\s,;|]+|[\s,;|]+$/g` is
    // quadratic on an INTERIOR separator run (the `$`-anchored alternative is
    // not start-anchored, so the engine retries the `+` from every index inside
    // the run) — measured 226ms at 20k separators and 5.4s at 100k, blocking,
    // on the SSR request path. Each anchored form can only match at one place.
    piece = piece.replace(LEADING_SEPARATORS_RE, '').replace(TRAILING_SEPARATORS_RE, '')
    if (piece === '') continue

    if (/^https?:\/\//i.test(piece)) {
      // A URL ends at the first whitespace; anything after it is a stray
      // label or the next line's non-URL text.
      let url = piece.replace(/\s[\s\S]*$/, '')
      url = url.replace(/[,;|]+$/, '')
      // dashboard#1519: a pasted hrimgs.co `plain/` URL. Its original URL is
      // the image; a bare prefix (original URL split off into the next
      // piece) is nothing on its own and is dropped.
      const unwrapped = unwrapProxiedUrl(url)
      if (unwrapped !== null) {
        afterProxyPrefix = unwrapped === ''
        if (unwrapped !== '' && looksLikeUrl(unwrapped)) urls.push(unwrapped)
        continue
      }
      if (afterProxyPrefix) {
        afterProxyPrefix = false
        url = stripProxyFormatSuffix(url)
      }
      if (looksLikeUrl(url)) urls.push(url)
      continue
    }
    afterProxyPrefix = false

    // No scheme: relative/protocol-relative paths, one per line or
    // separator. Kept only when they end in an image extension — free text
    // in this field must not become a broken <img>.
    for (const token of piece.split(/[\r\n]+|\s*[,;|]\s*/)) {
      const t = token.trim()
      if (looksLikeImagePath(t)) urls.push(t)
    }
  }

  // Legacy passthrough: a CDN path with no file extension used to render
  // as-is. Keep that, but it must LOOK like a path — at least one `/` — or the
  // carve-out swallows every bare word an operator types into an unfilled
  // field ("TBD", "N/A", "none", "-", "?") and turns it into a broken <img>
  // (and, for "?", an <img> that re-requests the whole page). That is exactly
  // what the scheme-less branch above refuses to do, one line earlier.
  if (urls.length === 0 && !isUnresolvableRootedPath(text) && LEGACY_PATH_RE.test(text) && !hasDangerousScheme(text)) {
    urls.push(text)
  }

  return Array.from(new Set(urls)).slice(0, IMAGE_FIELD_MAX_URLS)
}

// ── Image-typed custom-field registry ─────────────────────────────────
//
// The registry of image-typed custom-field names, matched against
// normalizeCustomFieldName(name) — lowercase, every non-alphanumeric
// character stripped, so `Bedroom 1 Image`, `bedroom1-image` and
// `bedroom1_image` all match. Extend this when a widget starts consuming
// another image-typed field. A bare suffix rule (`*image`) is deliberately
// NOT used: the PDP custom-fields block renders any mapped field as text,
// and a field named `Image credits` must stay text.
export const IMAGE_FIELD_NAME_PATTERNS: readonly RegExp[] = [/^bedroom\d+image$/]

/** True when `normalizedName` matches an entry in IMAGE_FIELD_NAME_PATTERNS. `''` is never an image field. */
export function isImageCustomFieldName(normalizedName: string): boolean {
  return normalizedName !== '' && IMAGE_FIELD_NAME_PATTERNS.some((pattern) => pattern.test(normalizedName))
}

/**
 * True when `field`'s name OR slug is an image-typed field name. Guards
 * `name`/`slug` being null on the opaque payload the way `indexCustomFields`
 * (common/custom-field-mapping.ts) does — `if (!raw) continue`.
 */
export function isImageCustomField(field: PublicCustomField): boolean {
  for (const raw of [field.name, field.slug]) {
    if (!raw) continue
    if (isImageCustomFieldName(normalizeCustomFieldName(raw))) return true
  }
  return false
}

/** The canonical join between an image field's parsed URLs. */
export const IMAGE_FIELD_VALUE_SEPARATOR = '\n'

/**
 * Normalize every image-typed field in `fields` to its canonical value: the
 * parsed URLs joined by IMAGE_FIELD_VALUE_SEPARATOR, or `''` when nothing
 * usable was pasted. `resolveCustomFields` (common/custom-field-mapping.ts)
 * drops blank values, so `''` hides the field in the PDP custom-fields block
 * instead of printing a raw paste or a placeholder like "TBD".
 *
 * The newline join is what makes this idempotent: `parseImageFieldValue`
 * splits on newlines (among other separators) ahead of everything else, so
 * `parseImageFieldValue(join(parseImageFieldValue(x)))` equals
 * `parseImageFieldValue(x)` — that property is what lets `resolveBedroomMedia`
 * keep parsing: a no-op on a hook-normalized value, a full parse on a raw
 * record. One documented edge (dashboard#1574 verifier, left alone as
 * contrived): a raw paste AT the IMAGE_FIELD_MAX_INPUT cap with no separator
 * at all re-parses with its last URL clipped by up to 19 bytes, because the
 * newline join is that much longer than the URL bytes the cap admitted.
 *
 * Pure: never mutates an input field or the input array. Returns the INPUT
 * ARRAY ITSELF (same reference) when no field was rewritten — including when
 * an image field's current value already equals its canonical form — so
 * react-query's structural sharing sees an unchanged result and downstream
 * memos do not re-run.
 */
export function normalizeImageCustomFields(fields: PublicCustomField[]): PublicCustomField[] {
  if (!Array.isArray(fields)) return []
  let changed = false
  const next = fields.map((field) => {
    if (!isImageCustomField(field)) return field
    const canonical = parseImageFieldValue(customFieldText(field.value)).join(IMAGE_FIELD_VALUE_SEPARATOR)
    if (canonical === field.value) return field
    changed = true
    return { ...field, value: canonical }
  })
  return changed ? next : fields
}
